import React, { useRef, useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import Main from "../redux/actions/main.action";
import { StateKeys } from "../redux/states/main.state";
import ApiEndpoint from "../services/api.endpoint.service";

export interface CustomVideoPlayerProps {
  /** Title shown above the video (e.g. "What is sound?") */
  title: string;
  /** URL of the video file */
  src: string;
  /** Content ID for posting watch points (optional) */
  contentId?: number;
  /** Resource ID for posting watch points (optional) */
  resourceId?: number;
  /** Called when this video finishes playing (e.g. for autoplay next) */
  onEnded?: () => void;
  /** When true, start playback when the video is ready (used by parent for autoplay next) */
  autoPlay?: boolean;
}

const SEEK_STEP_SEC = 10;
const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function CustomVideoPlayer({
  title,
  src,
  contentId,
  resourceId,
  onEnded: onEndedProp,
  autoPlay = false,
}: CustomVideoPlayerProps) {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const volumeControlRef = useRef<HTMLDivElement>(null);
  const pointPostedRef = useRef(false);
  const hasAutoPlayedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const hideControlsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const togglePlay = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
    } else {
      el.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying]);

  const skipBack = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, el.currentTime - SEEK_STEP_SEC);
    setCurrentTime(el.currentTime);
  }, []);

  const skipForward = useCallback(() => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = Math.min(el.duration || 0, el.currentTime + SEEK_STEP_SEC);
    setCurrentTime(el.currentTime);
  }, []);

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const el = videoRef.current;
      if (!el || !duration) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.max(0, Math.min(1, x / rect.width));
      el.currentTime = pct * duration;
      setCurrentTime(el.currentTime);
    },
    [duration],
  );

  const cycleSpeed = useCallback(() => {
    const i = SPEED_OPTIONS.indexOf(playbackRate);
    const next = SPEED_OPTIONS[(i + 1) % SPEED_OPTIONS.length];
    setPlaybackRate(next);
  }, [playbackRate]);

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!document.fullscreenElement) {
      container.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.playbackRate = playbackRate;
    el.muted = isMuted;
    el.volume = volume;
  }, [playbackRate, isMuted, volume]);

  useEffect(() => {
    if (!showVolumeSlider) return;
    const handleClick = (e: MouseEvent) => {
      if (
        volumeControlRef.current &&
        !volumeControlRef.current.contains(e.target as Node)
      ) {
        setShowVolumeSlider(false);
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [showVolumeSlider]);

  const postWatchPoint = useCallback(() => {
    if (pointPostedRef.current || contentId == null || resourceId == null)
      return;
    pointPostedRef.current = true;
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.USER_POINTS,
        apiEndpoint: ApiEndpoint.POINTS,
        req: {
          where: "watch",
          content_id: contentId,
          resource_id: resourceId,
          points: 1,
        },
      }),
    );
  }, [contentId, resourceId, dispatch]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onTimeUpdate = () => setCurrentTime(el.currentTime);
    const onLoadedMetadata = () => setDuration(el.duration);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      postWatchPoint();
      onEndedProp?.();
    };
    el.addEventListener("timeupdate", onTimeUpdate);
    el.addEventListener("loadedmetadata", onLoadedMetadata);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTimeUpdate);
      el.removeEventListener("loadedmetadata", onLoadedMetadata);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
    };
  }, [src, postWatchPoint, onEndedProp]);

  useEffect(() => {
    pointPostedRef.current = false;
  }, [src, contentId, resourceId]);

  useEffect(() => {
    hasAutoPlayedRef.current = false;
  }, [src]);

  useEffect(() => {
    if (!autoPlay) return;
    const el = videoRef.current;
    if (!el) return;
    const playWhenReady = () => {
      if (hasAutoPlayedRef.current) return;
      hasAutoPlayedRef.current = true;
      el.play().then(() => setIsPlaying(true));
    };
    if (el.readyState >= 2) {
      playWhenReady();
    } else {
      el.addEventListener("loadeddata", playWhenReady, { once: true });
      return () => el.removeEventListener("loadeddata", playWhenReady);
    }
  }, [autoPlay, src]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const handleMouseMove = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimerRef.current)
      clearTimeout(hideControlsTimerRef.current);
    hideControlsTimerRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
      hideControlsTimerRef.current = null;
    }, 3000);
  }, [isPlaying]);

  useEffect(() => {
    return () => {
      if (hideControlsTimerRef.current)
        clearTimeout(hideControlsTimerRef.current);
    };
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className="position-relative w-100 rounded overflow-hidden"
      style={{ paddingTop: "12px" }}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <p
        className="fw-semibold"
        style={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "20px",
          letterSpacing: "0.216px",
          color: "#3c3c3b",
          lineHeight: "20px",
          fontWeight: 600,
          marginBottom: "35px",
        }}
      >
        {title}
      </p>

      <div
        className="position-relative w-100 rounded overflow-hidden bg-black"
        style={{ aspectRatio: "16/9" }}
      >
        <video
          ref={videoRef}
          src={src}
          className="w-100 h-100"
          style={{ objectFit: "contain" }}
          playsInline
          onClick={togglePlay}
          preload="metadata"
        />

        {/* Bottom controls overlay */}
        <div
          className="position-absolute bottom-0 start-0 end-0 d-flex align-items-center px-2 px-md-3 py-2 gap-1 gap-md-2"
          style={{
            background: "linear-gradient(transparent, rgba(0,0,0,0.75))",
            opacity: showControls ? 1 : 0,
            transition: "opacity 0.2s ease",
          }}
        >
          <button
            type="button"
            className="btn btn-link p-0 border-0 text-white text-decoration-none"
            style={{ minWidth: "36px" }}
            onClick={skipBack}
            aria-label="Rewind 10 seconds"
          >
            <i className="las la-step-backward fs-5" />
          </button>

          <button
            type="button"
            className="btn btn-link p-0 border-0 text-white text-decoration-none"
            style={{ minWidth: "36px" }}
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            <i className={`las la-${isPlaying ? "pause" : "play"} fs-4`} />
          </button>

          <button
            type="button"
            className="btn btn-link p-0 border-0 text-white text-decoration-none"
            style={{ minWidth: "36px" }}
            onClick={skipForward}
            aria-label="Forward 10 seconds"
          >
            <i className="las la-step-forward fs-5" />
          </button>

          <button
            type="button"
            className="btn btn-link p-0 border-0 text-white text-decoration-none text-nowrap d-none d-sm-inline"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              minWidth: "40px",
            }}
            onClick={cycleSpeed}
            aria-label="Playback speed"
          >
            {playbackRate}x
          </button>

          <div
            className="flex-grow-1 position-relative d-flex align-items-center mx-1"
            style={{ minHeight: "20px" }}
          >
            <div
              role="progressbar"
              aria-valuenow={currentTime}
              aria-valuemin={0}
              aria-valuemax={duration}
              className="rounded-pill overflow-visible w-100 position-relative"
              style={{
                height: "6px",
                backgroundColor: "rgba(255,255,255,0.3)",
                cursor: "pointer",
              }}
              onClick={handleSeek}
            >
              <div
                className="h-100 rounded-pill"
                style={{
                  width: `${progress}%`,
                  backgroundColor: "#E76E4B",
                  transition: "width 0.1s linear",
                }}
              />
              <span
                className="position-absolute rounded-circle bg-white border border-2 border-white"
                style={{
                  width: "12px",
                  height: "12px",
                  top: "50%",
                  left: `${progress}%`,
                  transform: "translate(-50%, -50%)",
                  boxShadow: "0 0 2px rgba(0,0,0,0.3)",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>

          <span
            className="text-nowrap d-none d-md-inline"
            style={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "#fff",
              minWidth: "90px",
              textAlign: "right",
            }}
          >
            {formatTime(currentTime)}/{formatTime(duration)}
          </span>

          <div
            ref={volumeControlRef}
            className="position-relative d-flex align-items-center"
          >
            {showVolumeSlider && (
              <div
                className="position-absolute d-flex flex-column align-items-center gap-2 rounded"
                style={{
                  bottom: "100%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  marginBottom: "6px",
                  padding: "10px 8px",
                  backgroundColor: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  zIndex: 20,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.01}
                  value={isMuted ? 0 : volume}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setVolume(v);
                    setIsMuted(v === 0);
                  }}
                  aria-label="Volume"
                  style={{
                    width: "80px",
                    height: "6px",
                    cursor: "pointer",
                    accentColor: "#E76E4B",
                  }}
                />
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 border-0 text-decoration-none"
                  style={{ color: "#3c3c3b", fontSize: "12px" }}
                  onClick={() => setIsMuted(!isMuted)}
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? "Unmute" : "Mute"}
                </button>
              </div>
            )}
            <button
              type="button"
              className="btn btn-link p-0 border-0 text-white text-decoration-none"
              style={{ minWidth: "32px" }}
              onClick={() => setShowVolumeSlider((v) => !v)}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              <i className={`las la-volume-${isMuted ? "mute" : "up"} fs-5`} />
            </button>
          </div>

          <button
            type="button"
            className="btn btn-link p-0 border-0 text-white text-decoration-none"
            style={{ minWidth: "32px" }}
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            <i
              className={`las la-${isFullscreen ? "compress" : "expand"} fs-5`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomVideoPlayer;
